import {Controller} from "@hotwired/stimulus"
import {placeFloatingPanel} from "../../lib/floating_panel"

// Styled single-select for redesigned (ice-) screens.
//
// A native <select> cannot have its option list styled, so this renders a button
// plus a real options panel and keeps the value in a hidden input — the form
// still submits an ordinary param. Markup lives in
// app/views/shared/components/_ice_select.html.haml.
//
// The panel is position: fixed and placed from the trigger's rect rather than
// being absolutely positioned inside the wrapper. Any ancestor with
// overflow: hidden would otherwise clip it — the modal-modern layout puts
// ice:overflow-hidden on .modal-content, so an in-modal dropdown was cut off no
// matter which direction it opened. Fixed takes it out of that clipping context;
// position() then keeps it glued to the trigger on scroll and resize.

export default class extends Controller {
    static targets = ["input", "trigger", "label", "panel", "search", "option", "empty", "group"]
    static values = {submitOnChange: Boolean}

    connect() {
        this.onDocumentClick = (event) => {
            if (!this.element.contains(event.target)) this.close()
        }
        this.onViewportChange = () => this.position()
        this.onAddOption = (event) => this.addOption(event.detail || {})

        document.addEventListener("click", this.onDocumentClick)
        this.element.addEventListener("ice-select:add", this.onAddOption)
        this.syncOptionStyles()
    }

    disconnect() {
        document.removeEventListener("click", this.onDocumentClick)
        this.element.removeEventListener("ice-select:add", this.onAddOption)
        this.stopTrackingViewport()
    }

    //
    addOption({label, value, extra, select = true}) {
        const last = this.optionTargets[this.optionTargets.length - 1]
        if (!last) return

        const item = last.parentElement.cloneNode(true)
        const option = item.querySelector("[data-ice-select-target='option']")

        item.classList.remove("ice:hidden")
        option.dataset.value = value
        option.dataset.label = label

        const display = option.querySelector("[data-ice-select-display]")
        display.querySelector("span").textContent = label
        Array.from(display.children).slice(1).forEach((badge) => badge.remove())

        if (extra) {
            option.dataset.extra = JSON.stringify(extra)
        } else {
            delete option.dataset.extra
        }

        last.parentElement.after(item)

        if (select) requestAnimationFrame(() => this.select({preventDefault: () => {}, currentTarget: option}))
    }

    toggle(event) {
        event.preventDefault()
        this.isOpen ? this.close() : this.open()
    }

    open() {
        this.panelTarget.classList.remove("ice:hidden")
        this.triggerTarget.setAttribute("aria-expanded", "true")
        this.position()
        this.startTrackingViewport()

        if (this.hasSearchTarget) {
            this.searchTarget.value = ""
            this.filter()
            this.searchTarget.focus()
        } else {
            this.focusOption(this.selectedIndex >= 0 ? this.selectedIndex : 0)
        }
    }

    close() {
        if (!this.isOpen) return

        this.panelTarget.classList.add("ice:hidden")
        this.triggerTarget.setAttribute("aria-expanded", "false")
        this.stopTrackingViewport()
    }

    select(event) {
        event.preventDefault()
        const option = event.currentTarget

        this.inputTarget.value = option.dataset.value || ""
        this.labelTarget.classList.toggle("ice:text-slate-400", !option.dataset.value)

        const display = option.querySelector("[data-ice-select-display]")
        if (display) {
            this.labelTarget.innerHTML = display.innerHTML
        } else {
            this.labelTarget.textContent = option.dataset.label
        }

        this.applyExtras(option)

        this.inputTarget.dispatchEvent(new Event("change", {bubbles: true}))

        this.syncOptionStyles()
        this.close()
        this.triggerTarget.focus()

        if (this.submitOnChangeValue) this.submitForm()
    }

    submitForm() {
        const form = this.element.closest("form")
        if (!form) return

        // requestSubmit returns undefined, so this cannot be an || fallback —
        // that would submit twice.
        if (typeof form.requestSubmit === "function") {
            form.requestSubmit()
        } else {
            form.submit()
        }
    }

    applyExtras(option) {
        if (this.previousExtras) {
            this.previousExtras.forEach((key) => delete this.inputTarget.dataset[key])
        }
        this.previousExtras = []

        if (!option.dataset.extra) return

        let extras
        try {
            extras = JSON.parse(option.dataset.extra)
        } catch (error) {
            return
        }

        Object.entries(extras).forEach(([key, value]) => {
            const property = key.replace(/^data-/, "").replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
            this.inputTarget.dataset[property] = value
            this.previousExtras.push(property)
        })
    }

    filter() {
        const query = (this.searchTarget.value || "").trim().toLowerCase()
        let visible = 0

        this.optionTargets.forEach((option) => {
            const match = !query || (option.dataset.label || "").toLowerCase().includes(query)
            option.parentElement.classList.toggle("ice:hidden", !match)
            if (match) visible += 1
        })

        if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("ice:hidden", visible > 0)

        this.hideEmptyGroups()
    }

    hideEmptyGroups() {
        if (!this.hasGroupTarget) return

        this.groupTargets.forEach((group) => {
            let node = group.nextElementSibling
            let keep = false

            while (node && !node.matches("[data-ice-select-target='group']")) {
                if (node.querySelector("[data-ice-select-target='option']") && !node.classList.contains("ice:hidden")) {
                    keep = true
                    break
                }
                node = node.nextElementSibling
            }

            group.classList.toggle("ice:hidden", !keep)
        })
    }

    // Keyboard handling on the trigger and inside the panel.
    navigate(event) {
        switch (event.key) {
            case "Escape":
                this.close()
                this.triggerTarget.focus()
                break
            case "ArrowDown":
                event.preventDefault()
                this.isOpen ? this.moveFocus(1) : this.open()
                break
            case "ArrowUp":
                event.preventDefault()
                if (this.isOpen) this.moveFocus(-1)
                break
            case "Enter":
            case " ":
                if (!this.isOpen) {
                    event.preventDefault()
                    this.open()
                }
                break
        }
    }

    moveFocus(step) {
        const options = this.visibleOptions
        if (options.length === 0) return

        const current = options.indexOf(document.activeElement)
        const next = current === -1 ? 0 : (current + step + options.length) % options.length
        options[next].focus()
    }

    focusOption(index) {
        const options = this.visibleOptions
        if (options[index]) options[index].focus()
    }

    syncOptionStyles() {
        const value = this.inputTarget.value || ""

        this.optionTargets.forEach((option) => {
            const on = (option.dataset.value || "") === value
            option.setAttribute("aria-selected", on ? "true" : "false")
            option.classList.toggle("ice:bg-slate-100", on)
            option.classList.toggle("ice:font-semibold", on)
            option.classList.toggle("ice:text-slate-900", on)
            option.classList.toggle("ice:text-slate-700", !on)

            const check = option.querySelector("[data-ice-select-check]")
            if (check) check.classList.toggle("ice:invisible", !on)
        })
    }

    position() {
        if (!this.isOpen) return

        placeFloatingPanel(this.panelTarget, this.triggerTarget.getBoundingClientRect(), {matchWidth: true})
    }

    startTrackingViewport() {
        window.addEventListener("scroll", this.onViewportChange, {passive: true, capture: true})
        window.addEventListener("resize", this.onViewportChange, {passive: true})
    }

    stopTrackingViewport() {
        window.removeEventListener("scroll", this.onViewportChange, {capture: true})
        window.removeEventListener("resize", this.onViewportChange)
    }

    get isOpen() {
        return !this.panelTarget.classList.contains("ice:hidden")
    }

    get visibleOptions() {
        return this.optionTargets.filter((option) => !option.parentElement.classList.contains("ice:hidden"))
    }

    get selectedIndex() {
        return this.visibleOptions.findIndex((option) => option.getAttribute("aria-selected") === "true")
    }
}
